import { ApolloCache } from "@apollo/client"
import { stripTypename } from "@apollo/client/utilities"

import { MutationTypename } from "shared/api/types"
import { getObjectDifference } from "shared/lib/objectUtils"
import { sortByPosition } from "shared/lib/sorting/sortByPosition"
import { ProductPositionInstallationReferenceAPIType } from "shared/types/InstallationReference"
import { ProductPosition } from "shared/types/ProductPosition"

import { didMove } from "entities/project-children/lib/didMove"
import { getChildPositionData } from "entities/project-children/lib/getChildMoveData"

import { getProductPositionComplete } from "../lib/getProductPositionComplete"
import { readProductPositionFragment } from "../lib/readProductPositionFragment"
import { UpdateProductPositionParams } from "../types/ProductPosition"

export function prepareProductPositionUpdate({
  id,
  displayPosition,
  productPosition,
  addons,
  cache,
  projectID,
  parentID,
  childType = "ProductPosition"
}: {
  id: string
  projectID: string
  displayPosition?: number
  productPosition: UpdateProductPositionParams
  addons: ProductPosition["addons"]
  cache: ApolloCache<object>
  parentID?: string | null
  childType?: "ProductPosition"
}) {
  const cachedData = readProductPositionFragment(id, cache)

  if (!cachedData) {
    throw new Error(`Unable to read cached data for ProductPosition: ${id}`)
  }

  const changedValues = getObjectDifference(productPosition, cachedData || {})

  if (changedValues.variantKey) {
    changedValues.variantKey = changedValues.variantKey.toUpperCase()
  }

  const references = productPosition.references
    ? sortByPosition(
        productPosition.references.map(reference => ({
          type: ProductPositionInstallationReferenceAPIType,
          ...reference
        }))
      )
    : undefined

  const dimensioning = changedValues.dimensioning
    ? stripTypename(changedValues.dimensioning)
    : changedValues.dimensioning

  const moveData = getChildPositionData({
    projectID,
    parentID,
    childID: id,
    childType,
    displayPosition,
    cache
  })

  const updatedProductPosition: ProductPosition = {
    ...cachedData,
    ...productPosition,
    position:
      moveData.nextPosition !== -1
        ? moveData.nextPosition
        : cachedData.position,
    quantity: productPosition.quantity ?? cachedData.quantity,
    variantKey: productPosition.variantKey ?? cachedData.variantKey,
    addons
  }

  const optimisticProductPosition = getProductPositionComplete({
    productPosition: updatedProductPosition
  })

  const input: UpdateProductPositionParams & {
    addons: undefined
    position?: number
  } = {
    id,
    ...changedValues,
    // nextPosition === -1 only when nextParent is null (early-exit in getChildPositionData),
    // in which case didMove is already false — the extra guard is defensive.
    position:
      didMove(moveData) && moveData.nextPosition !== -1
        ? moveData.nextPosition
        : undefined,
    addons: undefined, // addons are derived from configuration, not sent in input
    references,
    dimensioning,
    parent:
      didMove(moveData) && moveData.prevParentID !== moveData.nextParentID
        ? {
            id: moveData.nextParentID,
            type: moveData.nextParentType
          }
        : undefined
  }

  return {
    input,
    cachedData,
    optimisticResponse: optimisticProductPosition
      ? {
          __typename: MutationTypename,
          productPosition: optimisticProductPosition
        }
      : undefined,
    moveData
  }
}
